import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { StudentMarksList, StudentProfile, StudentProfileRecord } from '@/types/student';
import { StudentSettings } from '@/features/student/components/StudentSettings';
import { toProfileDetails, type ProfileHeader } from './profileData';
import { ProfileHeroCard } from './ProfileHeroCard';
import { ProfileTabBar, type ProfileTab } from './ProfileTabBar';
import { ProfilePersonalInfoTab } from './ProfilePersonalInfoTab';
import { ProfileParentGuardianTab } from './ProfileParentGuardianTab';
import { ProfileAcademicDetailsTab } from './ProfileAcademicDetailsTab';
import { ProfileChangePasswordTab } from './ProfileChangePasswordTab';

interface StudentProfileViewProps {
  /**
   * The header the record is drawn under — class, section, session, class
   * teacher. The student panel passes its `/student/me`; the parent panel the
   * header its profile endpoint sends.
   */
  profile: ProfileHeader | null;
  /** The record itself: personal details, the household, the family. */
  record: StudentProfileRecord | null;
  /** Their papers, for the academic tab. Shared with the marks screen. */
  marks: StudentMarksList | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  /** Whose record this is, when the reader is not the child themselves. */
  childName?: string;
  /** The full profile record from `/student/me`, passed to settings. */
  studentProfile?: StudentProfile | null;
  /** Optional initial tab override. */
  initialTab?: ProfileTab;
  /** The child's photograph for the header badge, from a panel that can read it. */
  photo?: ReactNode;
}

/**
 * The child's own record, read from the office's copy of it.
 *
 * Nothing on this screen is editable, and that is the product's decision
 * rather than an omission: an admission record — a date of birth, a guardian's
 * number, an address — is the school office's to correct, and a panel that let
 * a child change their own would put the register and the roll out of step. A
 * correction is asked for at the office, which holds `/institute/students`.
 */
export function StudentProfileView({
  profile,
  record,
  marks,
  isLoading,
  error,
  onRetry,
  childName,
  studentProfile,
  initialTab,
  photo,
}: StudentProfileViewProps) {
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState<ProfileTab>(() => {
    if (requestedTab === 'settings' && !childName) return 'settings';
    if (requestedTab === 'password' && !childName) return 'password';
    if (requestedTab === 'academic') return 'academic';
    if (requestedTab === 'parent') return 'parent';
    return initialTab ?? 'personal';
  });

  useEffect(() => {
    if (requestedTab === 'settings' && !childName) setActiveTab('settings');
    else if (requestedTab === 'password' && !childName) setActiveTab('password');
    else if (requestedTab === 'academic') setActiveTab('academic');
    else if (requestedTab === 'parent') setActiveTab('parent');
    else if (requestedTab === 'personal') setActiveTab('personal');
  }, [requestedTab, childName]);

  // Rebuilt only when one of the two responses changes — the grouping and
  // grading below it run over every paper the child has sat.
  const details = useMemo(
    () => (record ? toProfileDetails(profile, record, marks?.exams ?? []) : null),
    [profile, record, marks],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5 max-w-[1400px] mx-auto w-full pb-12">
        <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-6">
          <SkeletonLines count={4} />
        </div>
        <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-6">
          <SkeletonLines count={8} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto w-full">
        <Alert
          tone="critical"
          title={childName ? `${childName}'s profile could not be loaded` : 'Your profile could not be loaded'}
        >
          <div className="flex flex-col items-start gap-3">
            <p>{error.message}</p>
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Try again
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  // Only reachable if the request succeeded and carried nothing, which means
  // the account is signed in but the office has not finished the record.
  if (!details) {
    return (
      <div className="max-w-[1400px] mx-auto w-full">
        <Alert
          tone="info"
          title={childName ? `${childName}'s record is not ready yet` : 'Your record is not ready yet'}
        >
          Your school office has not finished setting up {childName ? `${childName}'s` : 'your'}{' '}
          record. Ask them to check it.
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 animate-fade-in max-w-[1400px] mx-auto w-full pb-12">
      {/* 1. TOP HERO PROFILE CARD (Avatar, Name, Quick KPIs) */}
      <ProfileHeroCard details={details} photo={photo} />

      {/* 2. TAB NAVIGATION BAR */}
      <ProfileTabBar
        activeTab={activeTab}
        onSelectTab={setActiveTab}
        showPasswordTab={!childName}
        showSettingsTab={!childName}
      />

      {/* 3. ACTIVE TAB CONTENT VIEW */}
      {activeTab === 'personal' && (
        <ProfilePersonalInfoTab
          details={details}
          onOpenParentTab={() => setActiveTab('parent')}
          onOpenAcademicTab={() => setActiveTab('academic')}
          childName={childName}
        />
      )}

      {activeTab === 'parent' && <ProfileParentGuardianTab details={details} childName={childName} />}

      {activeTab === 'academic' && (
        <ProfileAcademicDetailsTab details={details} childName={childName} />
      )}

      {activeTab === 'password' && !childName && (
        <ProfileChangePasswordTab />
      )}

      {activeTab === 'settings' && !childName && (
        <StudentSettings profile={studentProfile ?? (profile as unknown as StudentProfile)} />
      )}
    </div>
  );
}
